import { fetchApi, fetchApiWithStatus } from "./api";
import { API_ENDPOINTS } from "../config/api";
import { pollJobUntilComplete, type JobProgressStatus } from "./jobsApi";
import type { ScrapeUrlResponse } from "../types/validation";

/** Scraper entry from GET /api/scrapers/sources */
export interface ScraperSource {
  available: boolean;
  base_url: string;
  name: string;
  source: string;
}

/** Response from GET /api/scrapers/sources */
export interface ScrapersSourcesResponse {
  scrapers: ScraperSource[];
}

export type ScraperRunStatus = "running" | "completed" | "failed";

/** Details of a scraper run from GET /api/scrapers/runs. */
export interface ScraperRun {
  id: number;
  source: string;
  status: ScraperRunStatus;
  started_at: string;
  completed_at?: string | null;
  products_found?: number | null;
  products_updated?: number | null;
  products_new?: number | null;
  error_message?: string | null;
  duration_seconds?: number | null;
}

/** Details of a scraper log entry from GET /api/scrapers/runs/{run_id}/logs. */
export interface ScraperLog {
  id: number;
  run_id?: number | null;
  level: string;
  message: string;
  details?: Record<string, unknown> | null;
  created_at: string;
}

export interface ScraperRunsOptions {
  source?: string;
  limit?: number;
}

export interface ScraperRunLogsOptions {
  runId: number;
  level?: string;
  limit?: number;
}

export async function getScrapersSources(): Promise<string[]> {
  const data = await fetchApi<ScrapersSourcesResponse>(
    API_ENDPOINTS.scrapersSources,
  );
  if (!data?.scrapers || !Array.isArray(data.scrapers)) return [];
  return data.scrapers
    .filter(
      (s): s is ScraperSource =>
        s && typeof s === "object" && typeof s.source === "string",
    )
    .map((s) => s.source);
}

export async function getScraperRuns(
  options: ScraperRunsOptions = {},
): Promise<ScraperRun[]> {
  const params = new URLSearchParams();
  if (options.source) params.set("source", options.source);
  if (options.limit) params.set("limit", String(options.limit));
  const query = params.toString();
  return fetchApi<ScraperRun[]>(
    `${API_ENDPOINTS.scrapersRuns}${query ? `?${query}` : ""}`,
  );
}

export async function getScraperRunLogs({
  runId,
  level,
  limit,
}: ScraperRunLogsOptions): Promise<ScraperLog[]> {
  const params = new URLSearchParams();
  if (level) params.set("level", level);
  if (limit) params.set("limit", String(limit));
  const query = params.toString();
  return fetchApi<ScraperLog[]>(
    `${API_ENDPOINTS.scrapersRunLogs(runId)}${query ? `?${query}` : ""}`,
  );
}

/** Options for POST /api/scrapers/scrape/product/{product_id} */
export interface ScrapeProductOptions {
  dry_run?: boolean;
  /** Called when job is queued or in progress (client-only). */
  onStatus?: (status: JobProgressStatus) => void;
}

/**
 * Scrape a product by its database ID (POST /api/scrapers/scrape/product/{product_id}).
 * Returns 202 with job_id; polls GET /api/jobs/{job_id} until complete, then returns result.
 */
export async function scrapeProductById(
  productId: number,
  options: ScrapeProductOptions = {},
): Promise<ScrapeUrlResponse> {
  const params = new URLSearchParams();
  if (options.dry_run) params.set("dry_run", "true");
  const url = `${API_ENDPOINTS.scrapeProduct(productId)}${params.toString() ? `?${params.toString()}` : ""}`;
  const { status, data } = await fetchApiWithStatus(url, { method: "POST" });

  if (status === 202) {
    const body = data as { job_id?: string };
    const jobId = body?.job_id;
    if (!jobId) throw new Error("Missing job_id in 202 response");
    return pollJobUntilComplete<ScrapeUrlResponse>(
      jobId,
      options.onStatus ? { onStatus: options.onStatus } : {},
    );
  }

  // Legacy 200 (if backend ever returns sync)
  return data as ScrapeUrlResponse;
}
